// Build-time import of all map icon URLs
const icons = import.meta.glob('images/old.map-icons/*.webp', {
    eager: true,
    as: 'url',
}) as Record<string, string>;

// Transform to a friendlier map keyed by the base filename (e.g., "de_ancient")
const iconsByName: Record<string, string> = Object.fromEntries(
    Object.entries(icons)
        .map(([path, url]) => {
            // Extract the final filename without extension from any kind of path
            // Matches last segment between / or \ and strips the extension
            const match = path.match(/[^\\/]+(?=\.[^\\/.]+$)/);
            const name = match ? match[0] : path;
            return [name, url as string];
        })
);

type MapIconOptions =
    "ar_baggage" |
    "ar_shoots" |
    "cs_italy" |
    "cs_office" |
    "de_ancient" |
    "de_cache"|
    "de_dust2" |
    "de_inferno" |
    "de_mirage" |
    "de_nuke" |
    "de_overpass" |
    "de_train"|
    "de_anubis"|
    "de_vertigo"

type ObjectFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';

export default function MapIcon(
    {
        name = 'de_ancient',
        alt,
        // Size controls
        size = 64, // default smaller size
        width,
        height,
        // Fill behavior
        fill = false,
        fit,
    }: {
        name?: MapIconOptions;
        alt?: string;
        /** If provided, used as both width and height unless width/height are set explicitly */
        size?: number | string;
        /** Explicit width (overrides size for width) */
        width?: number | string;
        /** Explicit height (overrides size for height) */
        height?: number | string;
        /** If true, image expands to fill its container (100% x 100%). Useful for full-area backgrounds. */
        fill?: boolean;
        /** CSS object-fit behavior. Defaults to 'cover' when fill=true, otherwise 'contain'. */
        fit?: ObjectFit;
    }
) {
    const url = iconsByName[name];
    if (!url) return null;

    const resolvedWidth = width ?? size;
    const resolvedHeight = height ?? size;

    const style: React.CSSProperties = fill
        ? { width: '100%', height: '100%', objectFit: fit ?? 'cover' }
        : { width: resolvedWidth, height: resolvedHeight, objectFit: fit ?? 'contain' };

    return <img src={url} alt={alt ?? `${name} icon`} style={style} />;
}
